// M14 Daily Lineup Board — dedicated "Daily Lineup" tab (distinct from Work
// Queue and M09 Lineup Management). Recruiter: today's board by default +
// calendar for any date, mark Going, mid-day company change, delivery status.
// Admin: same + recruiter filter + per-company email settings + full outbox.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ArrowRightLeft, Building2, CalendarDays, CheckCircle2, ChevronDown, Clock,
  History, Inbox, Mail, MailCheck, MailWarning, MailX, RefreshCw, Send, Settings2, UserX,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { KpiCard } from "@/components/common/KpiCard";
import { EmptyState } from "@/components/common/EmptyState";
import { WhatsAppCallbackButton } from "@/components/common/WhatsAppCallbackButton";
import { api, getErrorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const MAIL_BADGE = {
  "Sent": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: MailCheck },
  "Pending": { cls: "bg-sky-50 text-sky-700 border-sky-200", icon: Clock },
  "Queued - No Recipient": { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: MailWarning },
  "Failed": { cls: "bg-red-50 text-red-700 border-red-200", icon: MailX },
};

const MailBadge = ({ value, backedOut }) => {
  if (backedOut) {
    return (
      <Badge variant="outline" className="rounded-full bg-slate-100 text-slate-500 border-slate-200" data-testid="entry-mail-badge">
        <UserX className="h-3 w-3 mr-1" /> Backed out
      </Badge>
    );
  }
  const m = MAIL_BADGE[value] || MAIL_BADGE.Pending;
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`rounded-full ${m.cls}`} data-testid="entry-mail-badge">
      <Icon className="h-3 w-3 mr-1" /> {value}
    </Badge>
  );
};

const toISO = (d) => {
  const off = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return off.toISOString().slice(0, 10);
};

export default function LineupBoard() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "super_admin";

  const [date, setDate] = useState(null); // null = today (server IST)
  const [calOpen, setCalOpen] = useState(false);
  const [board, setBoard] = useState(null);
  const [eligibleList, setEligibleList] = useState(null);
  const [options, setOptions] = useState(null);
  const [status, setStatus] = useState(null);
  const [outboxItems, setOutboxItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recruiterFilter, setRecruiterFilter] = useState("all");
  const [tab, setTab] = useState("board"); // board | outbox | settings(admin)

  const [moveFor, setMoveFor] = useState(null);
  const [moveCompany, setMoveCompany] = useState("");
  const [moveReason, setMoveReason] = useState("");
  const [backOutFor, setBackOutFor] = useState(null);
  const [backOutReason, setBackOutReason] = useState("");
  // M17 P3: Interview happened -> Selection
  const [interviewFor, setInterviewFor] = useState(null);
  const [interviewNote, setInterviewNote] = useState("");
  const [historyFor, setHistoryFor] = useState(null);
  const [historyEvents, setHistoryEvents] = useState(null);
  const [busy, setBusy] = useState(false);

  // admin email settings
  const [companyEmails, setCompanyEmails] = useState(null);
  const [editEmailsFor, setEditEmailsFor] = useState(null);
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");

  const isToday = !date || date === status?.today;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { date: date || undefined };
    if (isAdmin && recruiterFilter !== "all") params.recruiter_id = recruiterFilter;
    Promise.all([
      api.get("/lineup-board", { params }),
      api.get("/lineup-board/eligible", { params }),
      api.get("/lineup-board/status"),
    ])
      .then(([b, e, s]) => { setBoard(b.data); setEligibleList(e.data); setStatus(s.data); })
      .catch((err) => setError(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [date, recruiterFilter, isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/lineup-board/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === "outbox") {
      api.get("/lineup-board/emails", { params: { date: date || status?.today || undefined } })
        .then((r) => setOutboxItems(r.data)).catch((e) => toast.error(getErrorMessage(e)));
    }
    if (tab === "settings" && isAdmin) {
      api.get("/lineup-board/company-emails")
        .then((r) => setCompanyEmails(r.data)).catch((e) => toast.error(getErrorMessage(e)));
    }
  }, [tab, date, status?.today, isAdmin]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
      return true;
    } catch (e) {
      toast.error(getErrorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const markGoing = (e) => act(
    () => api.post("/lineup-board/going", {
      application_id: e.application_id, company_id: e.company_id,
      lineup_id: e.lineup_id, job_title: e.job_title, date: date || undefined,
    }),
    `${e.candidate_name} confirmed Going — email goes out ${isToday ? "in the next send" : "on that date"}`);

  const submitMove = async () => {
    if (!moveCompany || moveReason.trim().length < 3) {
      toast.error("Select the new company and give a short reason"); return;
    }
    const ok = await act(
      () => api.put(`/lineup-board/${moveFor.id}/company`, { company_id: moveCompany, reason: moveReason.trim() }),
      "Company updated — the new company gets this candidate in the next 15-minute batch");
    if (ok) { setMoveFor(null); setMoveCompany(""); setMoveReason(""); }
  };

  const submitBackOut = async () => {
    if (backOutReason.trim().length < 3) { toast.error("Give a short reason"); return; }
    const ok = await act(
      () => api.post(`/lineup-board/${backOutFor.id}/back-out`, { reason: backOutReason.trim() }),
      "Backed out — application closed. Reassign from the Candidate Directory to re-engage.");
    if (ok) { setBackOutFor(null); setBackOutReason(""); }
  };

  const submitInterviewHappened = async () => {
    const ok = await act(
      () => api.post(`/recruiter-flow/going/${interviewFor.id}/interview-happened`,
        { note: interviewNote.trim() || undefined }),
      `${interviewFor.candidate_name} moved to Selection — awaiting admin decision`);
    if (ok) { setInterviewFor(null); setInterviewNote(""); }
  };

  const showHistory = (g) => {
    setHistoryFor(g); setHistoryEvents(null);
    api.get(`/lineup-board/${g.id}/history`)
      .then((r) => setHistoryEvents(r.data))
      .catch((e) => { toast.error(getErrorMessage(e)); setHistoryFor(null); });
  };

  const openEmailEditor = (c) => {
    setEditEmailsFor(c);
    setToInput((c.lineup_to_emails || []).join(", "));
    setCcInput((c.lineup_cc_emails || []).join(", "));
  };

  const saveEmails = async () => {
    const to = toInput.split(",").map((e) => e.trim()).filter(Boolean);
    if (to.length === 0) { toast.error("At least one To email is required"); return; }
    setBusy(true);
    try {
      await api.put(`/lineup-board/company-emails/${editEmailsFor.id}`, {
        to_emails: to, cc_emails: ccInput.split(",").map((e) => e.trim()).filter(Boolean),
      });
      toast.success(`Recipients saved for ${editEmailsFor.name} — queued mail sends in the next batch`);
      setEditEmailsFor(null);
      api.get("/lineup-board/company-emails").then((r) => setCompanyEmails(r.data)).catch(() => {});
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const sendNow = () => act(
    () => api.post("/lineup-board/run"),
    "Send pass triggered — check the outbox for results");

  const kpis = useMemo(() => {
    const g = board?.groups || [];
    const sum = (k) => g.reduce((a, x) => a + x[k], 0);
    return { total: board?.total || 0, sent: sum("sent"), pending: sum("pending") + sum("queued"),
             failed: sum("failed") };
  }, [board]);

  const displayDate = board?.date || status?.today || "";

  return (
    <div className="space-y-6" data-testid={`lineup-board-page-${isAdmin ? "admin" : "recruiter"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-primary" /> {isAdmin ? "Daily Lineup" : "Going"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Confirm who's Going today — emails go out automatically at {status?.daily_send_time || "11:15 IST"},
            then every {status?.batch_minutes || 15} minutes for new confirmations.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="board-date-picker">
                <CalendarDays className="h-3.5 w-3.5" />
                {isToday ? `Today · ${displayDate}` : displayDate}
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={date ? new Date(date) : new Date()}
                        onSelect={(d) => { if (d) { setDate(toISO(d)); setCalOpen(false); } }} />
              <div className="border-t border-border p-2">
                <Button variant="ghost" size="sm" className="w-full" data-testid="board-jump-today"
                        onClick={() => { setDate(null); setCalOpen(false); }}>
                  Jump to today
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          {isAdmin && (
            <Select value={recruiterFilter} onValueChange={setRecruiterFilter}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="board-recruiter-filter">
                <SelectValue placeholder="Recruiter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All recruiters</SelectItem>
                {(options?.recruiters || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={load} data-testid="board-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
          {isAdmin && (
            <Button size="sm" className="gap-1.5" onClick={sendNow} disabled={busy} data-testid="board-send-now">
              <Send className="h-3.5 w-3.5" /> Send now
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Going today" value={kpis.total} icon={CalendarDays} tone="primary" testId="kpi-going" />
        <KpiCard label="Emailed" value={kpis.sent} icon={MailCheck} tone="success" testId="kpi-emailed" />
        <KpiCard label="Awaiting send" value={kpis.pending} icon={Clock} tone="warning" testId="kpi-awaiting" />
        <KpiCard label="Failed" value={kpis.failed} icon={MailX} tone="danger" testId="kpi-failed" />
      </div>

      {status && status.mailer_enabled === false && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2" data-testid="mailer-off-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Auto-mailer is OFF — no lineup emails are being sent.
          {isAdmin
            ? " Turn it ON in Admin Settings → System & Backups when you're ready to go live."
            : " Going confirmations are saved and will be emailed once your admin activates sending."}
        </div>
      )}

      {status && status.senders_configured < 2 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2" data-testid="sender-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Only {status.senders_configured} sender account configured — failover needs both Gmail accounts in the backend settings.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="board" data-testid="tab-board">Board</TabsTrigger>
          <TabsTrigger value="outbox" data-testid="tab-outbox">Email log</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="tab-email-settings">Email settings</TabsTrigger>}
        </TabsList>
      </Tabs>

      {tab === "board" && (loading ? (
        <div className="space-y-3" data-testid="board-loading">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
        </div>
      ) : error ? (
        <Card><CardContent className="p-10 text-center" data-testid="board-error">
          <p className="text-sm text-red-600 mb-3">{error}</p>
          <Button variant="outline" onClick={load}>Retry</Button>
        </CardContent></Card>
      ) : (
        <>
          {/* eligible (not yet confirmed) */}
          {(eligibleList || []).length > 0 && (
            <Card data-testid="eligible-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" /> On {isToday ? "today's" : "this date's"} lineups — awaiting Going confirmation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border">
                  {eligibleList.map((e) => (
                    <li key={`${e.lineup_id}-${e.application_id}`} className="py-2.5 flex flex-wrap items-center gap-3"
                        data-testid={`eligible-${e.app_code}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{e.candidate_name}
                          <span className="text-xs text-muted-foreground font-normal"> · {e.job_title} · {e.company_name}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">{e.lineup_code} · {e.app_code}
                          {isAdmin && e.recruiter_name ? ` · ${e.recruiter_name}` : ""}</p>
                      </div>
                      <Button size="sm" className="gap-1.5" disabled={busy}
                              onClick={() => markGoing(e)} data-testid={`mark-going-${e.app_code}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Mark Going
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* board grouped by company */}
          {(board?.groups || []).length === 0 ? (
            <Card><CardContent className="py-14" data-testid="board-empty">
              <EmptyState icon={Inbox} title={`No candidates marked Going for ${displayDate}`}
                          description="Candidates on a Ready/Sent lineup for this date appear above — confirm them with one click." />
            </CardContent></Card>
          ) : (
            <div className="space-y-4" data-testid="board-groups">
              {board.groups.map((grp) => (
                <Card key={grp.company_id} data-testid={`company-group-${grp.company_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary" /> {grp.company_name}
                        <Badge variant="secondary" className="rounded-full">{grp.entries.length}</Badge>
                      </CardTitle>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {grp.sent > 0 && <span className="text-emerald-600 font-medium">{grp.sent} emailed</span>}
                        {grp.pending > 0 && <span>· {grp.pending} awaiting</span>}
                        {grp.queued > 0 && <span className="text-amber-600 font-medium">· {grp.queued} queued (no recipient)</span>}
                        {grp.failed > 0 && <span className="text-red-600 font-medium">· {grp.failed} failed</span>}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Candidate</TableHead>
                          <TableHead>Profile</TableHead>
                          {isAdmin && <TableHead>Recruiter</TableHead>}
                          <TableHead>Email status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {grp.entries.map((g) => (
                          <TableRow key={g.id} data-testid={`board-entry-${g.going_code}`}>
                            <TableCell>
                              <p className="font-medium">{g.candidate_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{g.going_code} · {g.candidate_mobile || "—"}</p>
                            </TableCell>
                            <TableCell className="text-sm">{g.job_title || "—"}</TableCell>
                            {isAdmin && <TableCell className="text-sm">{g.recruiter_name || "—"}</TableCell>}
                            <TableCell>
                              <MailBadge value={g.mail_status} backedOut={g.board_status === "Backed Out"} />
                              {g.sent_email_code && (
                                <p className="text-[11px] text-muted-foreground font-mono mt-1">{g.sent_email_code}</p>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {g.board_status === "Interview Happened" && (
                                  <Badge variant="outline" className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-200 h-8 px-2.5"
                                         data-testid={`interview-done-${g.going_code}`}>
                                    <CheckCircle2 className="h-3 w-3 mr-1" /> Interview happened
                                  </Badge>
                                )}
                                {g.board_status === "Going" && !g.interview_happened_at && (
                                  <>
                                    <Button size="sm" className="gap-1 h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                                            onClick={() => setInterviewFor(g)}
                                            data-testid={`interview-happened-${g.going_code}`}>
                                      <CheckCircle2 className="h-3.5 w-3.5" /> Interview happened
                                    </Button>
                                    <Button variant="outline" size="sm" className="gap-1 h-8"
                                            onClick={() => { setMoveFor(g); setMoveCompany(""); setMoveReason(""); }}
                                            data-testid={`change-company-${g.going_code}`}>
                                      <ArrowRightLeft className="h-3.5 w-3.5" /> Company
                                    </Button>
                                    {g.mail_status === "Failed" && (
                                      <Button variant="outline" size="sm" className="h-8 gap-1 text-amber-700"
                                              onClick={() => act(() => api.post(`/lineup-board/${g.id}/requeue`), "Requeued for the next batch")}
                                              data-testid={`requeue-${g.going_code}`}>
                                        <RefreshCw className="h-3.5 w-3.5" /> Requeue
                                      </Button>
                                    )}
                                    <Button variant="ghost" size="sm" className="h-8 gap-1 text-muted-foreground"
                                            onClick={() => { setBackOutFor(g); setBackOutReason(""); }}
                                            data-testid={`back-out-${g.going_code}`}>
                                      <UserX className="h-3.5 w-3.5" /> Backed out
                                    </Button>
                                    <WhatsAppCallbackButton candidateName={g.candidate_name} candidateMobile={g.candidate_mobile} className="h-8" />
                                  </>
                                )}
                                {g.board_status === "Backed Out" && (
                                  <Button variant="outline" size="sm" className="h-8 gap-1 text-emerald-700"
                                          disabled={busy}
                                          onClick={() => act(() => api.post("/lineup-board/going", {
                                            application_id: g.application_id, company_id: g.company_id,
                                            job_title: g.job_title, date: date || undefined,
                                          }), `${g.candidate_name} lined up again for ${g.company_name}`)}
                                          data-testid={`line-up-again-${g.going_code}`}>
                                    <Send className="h-3.5 w-3.5" /> Line up again
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => showHistory(g)}
                                        data-testid={`entry-history-${g.going_code}`} aria-label="History">
                                  <History className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ))}

      {tab === "outbox" && (
        <Card data-testid="outbox-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" /> Email delivery log — {displayDate}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!outboxItems ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}</div>
            ) : outboxItems.length === 0 ? (
              <EmptyState icon={Mail} title="No emails for this date"
                          description="Every automatic and manual send attempt appears here with its result." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Candidates</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>Sender account</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outboxItems.map((m) => (
                    <TableRow key={m.id} data-testid={`outbox-row-${m.email_code}`}>
                      <TableCell>
                        <p className="font-mono text-xs">{m.email_code}</p>
                        <p className="text-xs text-muted-foreground">{m.is_update ? "Update batch" : "Daily send"} · To: {m.to.join(", ")}</p>
                      </TableCell>
                      <TableCell className="text-sm">{m.company_name}</TableCell>
                      <TableCell className="text-sm">{m.candidate_count}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-full ${m.status === "Sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                          {m.status === "Sent" ? <MailCheck className="h-3 w-3 mr-1" /> : <MailX className="h-3 w-3 mr-1" />}
                          {m.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {m.sender_used ? (
                          <span className="capitalize">{m.sender_used}</span>
                        ) : "—"}
                        {(m.attempts || []).length > 1 && (
                          <p className="text-[11px] text-amber-600">after {m.attempts.length - 1} failed attempt(s)</p>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDateTime(m.sent_at || m.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "settings" && isAdmin && (
        <Card data-testid="email-settings-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-primary" /> Company lineup recipients
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!companyEmails ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}</div>
            ) : companyEmails.length === 0 ? (
              <EmptyState icon={Building2} title="No companies yet" description="Companies appear here once created." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>CC</TableHead>
                    <TableHead className="text-right">Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companyEmails.map((c) => (
                    <TableRow key={c.id} data-testid={`company-emails-row-${c.id}`}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell className="text-sm">
                        {c.lineup_to_emails.length ? c.lineup_to_emails.join(", ") : (
                          <span className="text-amber-600 flex items-center gap-1 text-xs font-medium">
                            <AlertTriangle className="h-3.5 w-3.5" /> Missing — emails will queue
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.lineup_cc_emails.join(", ") || "—"}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => openEmailEditor(c)}
                                data-testid={`edit-emails-${c.id}`}>Edit</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* change company dialog */}
      <Dialog open={!!moveFor} onOpenChange={(v) => !v && setMoveFor(null)}>
        <DialogContent className="max-w-md" data-testid="change-company-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-primary" /> Change company — {moveFor?.candidate_name}
            </DialogTitle>
            <DialogDescription>
              The new company receives this candidate in the next 15-minute batch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Now going to</Label>
              <Select value={moveCompany} onValueChange={setMoveCompany}>
                <SelectTrigger data-testid="move-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {(options?.companies || []).filter((c) => c.id !== moveFor?.company_id).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{!c.has_recipients ? " (no email set — will queue)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(options?.companies || []).filter((c) => c.id !== moveFor?.company_id).length === 0 && (
                <p className="text-xs text-amber-700" data-testid="board-move-no-companies-note">
                  No other {!isAdmin ? "allocated " : ""}companies available{!isAdmin ? " — ask your admin to allocate another company" : ""}.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Textarea rows={2} value={moveReason} onChange={(e) => setMoveReason(e.target.value)}
                        placeholder="e.g. Candidate backed out of the original company" data-testid="move-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveFor(null)}>Cancel</Button>
            <Button onClick={submitMove} disabled={busy} data-testid="move-submit">Update company</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* back out dialog */}
      <Dialog open={!!backOutFor} onOpenChange={(v) => !v && setBackOutFor(null)}>
        <DialogContent className="max-w-md" data-testid="back-out-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserX className="h-4 w-4 text-primary" /> Backed out — {backOutFor?.candidate_name}
            </DialogTitle>
            <DialogDescription>The application will be closed and the candidate returned to the Candidate Directory — reassign from there to re-engage later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Textarea rows={2} value={backOutReason} onChange={(e) => setBackOutReason(e.target.value)}
                      placeholder="Why is the candidate not going?" data-testid="back-out-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBackOutFor(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitBackOut} disabled={busy} data-testid="back-out-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* M17 P3: interview happened dialog */}
      <Dialog open={!!interviewFor} onOpenChange={(v) => !v && setInterviewFor(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="interview-happened-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Interview happened — {interviewFor?.candidate_name}
            </DialogTitle>
            <DialogDescription>
              Confirms the interview at {interviewFor?.company_name} took place. The candidate moves to the
              Selection tab where the admin decides Joined / Not Interested / Reschedule.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={interviewNote} onChange={(e) => setInterviewNote(e.target.value)}
                      placeholder="e.g. Interview went well, HR said they'll confirm by Friday"
                      data-testid="interview-happened-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInterviewFor(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitInterviewHappened}
                    disabled={busy} data-testid="interview-happened-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* history dialog */}
      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="max-w-lg" data-testid="entry-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-primary" /> History — {historyFor?.going_code}
            </DialogTitle>
            <DialogDescription>Immutable trail: confirmations, company changes and every email attempt.</DialogDescription>
          </DialogHeader>
          {!historyEvents ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
          ) : (
            <ol className="relative border-l border-border ml-2 space-y-4 max-h-[50vh] overflow-y-auto pr-2">
              {historyEvents.map((e) => (
                <li key={e.id} className="ml-4">
                  <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                  <p className="text-sm font-medium">{e.event_type.replaceAll("_", " ")}</p>
                  {e.remarks && <p className="text-xs text-muted-foreground">{e.remarks}</p>}
                  {e.details?.sender_account && (
                    <p className="text-xs text-muted-foreground">Sent via {e.details.sender_account} account</p>
                  )}
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5">{e.actor_name} · {formatDateTime(e.at)}</p>
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>

      {/* admin: edit company recipients */}
      <Dialog open={!!editEmailsFor} onOpenChange={(v) => !v && setEditEmailsFor(null)}>
        <DialogContent className="max-w-md" data-testid="edit-emails-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" /> Lineup recipients — {editEmailsFor?.name}
            </DialogTitle>
            <DialogDescription>Comma-separate multiple addresses. Queued emails send automatically after saving.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>To (required)</Label>
              <Input value={toInput} onChange={(e) => setToInput(e.target.value)}
                     placeholder="hr@company.com, hiring@company.com" data-testid="emails-to-input" />
            </div>
            <div className="space-y-1.5">
              <Label>CC</Label>
              <Input value={ccInput} onChange={(e) => setCcInput(e.target.value)}
                     placeholder="Optional" data-testid="emails-cc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmailsFor(null)}>Cancel</Button>
            <Button onClick={saveEmails} disabled={busy} data-testid="emails-save">Save recipients</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
